import { Alert, Divider, Paper, useTheme } from "@mui/material";
import useStyles from "./styles";
import { useEffect, useState } from "react";
import ServiceHeaderHostsConfig from "./serviceHeader";
import HostsSelector from "./hostsSelector";
import OnusSelector from "./onusSelector";
import api from "../../../../../services/api";
import LinkSelector from "./linkSelector";
import { set } from "zod";
import { DeviceHubOutlined, WifiOutlined } from "@mui/icons-material";
import PreviewHostsConfig from "../previewDetails";
import ModalConfig from "./modalConfig";
import { TAG_COLORS } from "./interfaceTags";

export default function DetailsViewHostsConfig({
  //
  loading = {},
  changed = false,
  initialData = {},
  clienteInfo = {},
  interfaceList = {},
  availableHosts = {},
  selectedService = {},
  filteredServices = [],
  availableDevices = {},
  pendingNavigation = {},
  setConfirmacaoSaidaPagina = () => {},
  setSelectedService = () => {},
  setInterfaceList = () => {},
  setSnackbar = () => {},
  setEditMode = () => {},
  setChanged = () => {},
  editMode = false,
  syncRunning = false,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [onusFromErp, setOnusFromErp] = useState([]);
  const [manipulateCpes, setManipulateCpes] = useState(
    initialData?.cpes?.[selectedService?.id] || [],
  );
  const [manipulateOnus, setManipulateOnus] = useState(
    initialData?.onus?.[selectedService?.id] || [],
  );
  const [manipulateOnuItems, setManipulateOnuItems] = useState([]);
  const [blockedHostIds, setBlockedHostIds] = useState([]);
  const [manipulateLinks, setManipulateLinks] = useState([]);
  const [manipulateSessions, setManipulateSessions] = useState([]);
  const [onuValid, setOnuValid] = useState(true);
  // Definições de tags de interface (por cliente) — usadas em CPE/link/sessão
  const [interfaceTags, setInterfaceTags] = useState([]);

  // Quando serviço mudar:
  useEffect(() => {
    setManipulateCpes(initialData?.cpes?.[selectedService?.id] || []);
    setManipulateOnus(initialData?.onus?.[selectedService?.id] || []);
    setManipulateLinks(initialData?.links?.[selectedService?.id] || []);
    setManipulateSessions(initialData?.sessions?.[selectedService?.id] || []);
    setManipulateOnuItems(
      initialData?.onus?.[selectedService?.id]?.map((onu) => onu?.onuItem) ||
        [],
    );
    setOnuValid(true);
    if (!!selectedService?.id) {
      // obter hosts e onus
      getOnuData({
        selectedService,
        clienteId: selectedService?.ownerClienteId || clienteInfo?.id,
        setOnusFromErp,
        setSnackbar,
        loading,
      });
      // obter interfaces
      getManyInterfaces({
        setSnackbar,
        setInterfaceList,
        manipulateCpes: initialData?.cpes?.[selectedService?.id] || [],
        manipulateSessions: initialData?.sessions?.[selectedService?.id] || [],
        manipulateLinks: initialData?.links?.[selectedService?.id] || [],
        interfaceList,
        loading,
        api,
      });
    } else {
      setOnusFromErp([]);
    }
    setEditMode(false);
  }, [selectedService]);

  // Detectar hosts selecionados em otros serviços
  useEffect(() => {
    if (selectedService?.id) {
      let resultado = [];
      Object.entries(initialData.cpes)?.forEach(([key, groups]) => {
        // Ignora o serviço selecionado
        if (key === String(selectedService?.id)) return;
        groups?.forEach((group) => {
          group?.selectedHosts?.forEach((host) => {
            const serviceInfo = filteredServices?.find(
              (service) => String(service?.id) === key,
            );
            resultado.push({
              hostid: host?.hostId || host?.hostid,
              servico: `${key} - ${serviceInfo?.nome ?? ""}`,
            });
          });
        });
      });

      setBlockedHostIds(resultado);
    }
  }, [
    initialData?.cpes?.[selectedService?.id],
    initialData?.onus?.[selectedService?.id],
    selectedService?.id,
    selectedService,
  ]);

  // Detectar se houveram alterações
  useEffect(() => {
    if (!Object.values(loading).some((value) => !!value)) {
      setChanged(true);
    }
  }, [
    manipulateCpes,
    manipulateOnus,
    manipulateOnuItems,
    manipulateLinks,
    manipulateSessions,
  ]);

  /** TAGS DE INTERFACE */
  // Buscar definições de tags (por cliente)
  useEffect(() => {
    if (!clienteInfo?.id) return;
    (async () => {
      try {
        const response = await api.post("/interno/findManyTagsInterface", {
          clienteId: clienteInfo?.id,
        });
        setInterfaceTags(response?.data || []);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [clienteInfo?.id]);

  // Criar tag (retorna a tag criada para atribuição imediata)
  const createTag = async (tagName) => {
    const name = tagName?.trim();
    if (!name) return null;
    if (
      interfaceTags?.some(
        (t) => t?.tagName?.trim()?.toLowerCase() === name.toLowerCase(),
      )
    ) {
      setSnackbar({ children: "Tag já existe", severity: "info" });
      return null;
    }
    try {
      const colorTag =
        TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];
      const response = await api.post("/interno/create/tagInterface", {
        clienteId: clienteInfo?.id,
        tagName: name,
        colorTag,
      });
      if (response?.data) {
        setInterfaceTags((prev) => [...prev, response.data]);
        setSnackbar({ children: "Tag criada com sucesso", severity: "success" });
        return response.data;
      }
    } catch (error) {
      console.error(error);
      setSnackbar({ children: "Erro ao criar tag", severity: "error" });
    }
    return null;
  };

  // Editar tag (nome/cor) — reflete ao vivo nos chips via resolução por id
  const editTag = async (tag) => {
    if (!tag?.id || !tag?.tagName?.trim()) return;
    try {
      const response = await api.post("/interno/edit/tagInterface", {
        tagId: tag.id,
        tagName: tag.tagName.trim(),
        colorTag: tag.colorTag,
      });
      if (response?.data) {
        setInterfaceTags((prev) =>
          prev.map((t) => (t.id === response.data.id ? response.data : t)),
        );
        setSnackbar({ children: "Tag editada com sucesso", severity: "success" });
      }
    } catch (error) {
      console.error(error);
      setSnackbar({ children: "Erro ao editar tag", severity: "error" });
    }
  };

  // Excluir tag — some dos chips (resolução por id) e some no save (filtro)
  const deleteTag = async (tagId) => {
    if (!tagId) return;
    try {
      await api.post("/interno/delete/tagInterface", { tagId });
      setInterfaceTags((prev) => prev.filter((t) => t.id !== tagId));
      setSnackbar({ children: "Tag deletada", severity: "success" });
    } catch (error) {
      console.error(error);
      setSnackbar({ children: "Erro ao deletar tag", severity: "error" });
    }
  };

  // Obter interfaces/sessões
  const getInterfaces = async ({ hostId, type, url }) => {
    const res = await getInterfaceList({
      setInterfaceList,
      setSnackbar,
      interfaceList,
      loading,
      hostId,
      type,
      url,
      api,
    });
  };

  /** EDITAR CONFIGURAÇÕES */
  // Desfazer alterações
  const handleCancel = () => {
    setEditMode(false);
    setManipulateCpes(initialData?.cpes?.[selectedService?.id] || []);
    setManipulateOnus(initialData?.onus?.[selectedService?.id] || []);
    setManipulateLinks(initialData?.links?.[selectedService?.id] || []);
    setManipulateSessions(initialData?.sessions?.[selectedService?.id] || []);
    setManipulateOnuItems(
      initialData?.onus?.[selectedService?.id]?.map((onu) => onu?.onuItem) ||
        [],
    );
    setOnuValid(true);
    setChanged(false);
  };

  // Salvar alterações
  const saveConfig = () => {
    // console.log({
    //   availableDevices,
    //   availableHosts,
    //   initialData,
    //   manipulateOnus,
    //   selectedService,
    //   manipulateOnuItems,
    //   manipulateCpes,
    //   blockedHostIds,
    //   filteredServices,
    // });
    handleSave({
      manipulateLinks,
      manipulateSessions,
      setManipulateCpes,
      setManipulateOnus,
      selectedService,
      manipulateCpes,
      manipulateOnus,
      initialData,
      clienteInfo,
      setSnackbar,
      setChanged,
      setEditMode,
      loading,
      interfaceTags,
    });
  };

  const icons = {
    links: <WifiOutlined color="primary" sx={styles.addDeviceIcon} />,
    sessions: <DeviceHubOutlined color="primary" sx={styles.addDeviceIcon} />,
  };

  return (
    <Paper
      elevation={theme.palette.mode === "dark" ? 0 : 3}
      sx={styles.paperFluxo}
    >
      {/** Header e botões de ação */}
      <ServiceHeaderHostsConfig
        loading={loading}
        changed={changed}
        editMode={editMode}
        handleSave={saveConfig}
        setEditMode={setEditMode}
        handleCancel={handleCancel}
        selectedService={selectedService}
        setSelectedService={setSelectedService}
        setConfirmacaoSaidaPagina={setConfirmacaoSaidaPagina}
        pendingNavigation={pendingNavigation}
        syncRunning={syncRunning}
      />

      {syncRunning && (
        <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
          Edição desabilitada temporariamente durante a sincronização global de ONUs.
        </Alert>
      )}

      <PreviewHostsConfig
        loading={loading}
        editMode={editMode}
        initialData={initialData}
        clienteInfo={clienteInfo}
        selectedService={selectedService}
        manipulateCpes={manipulateCpes}
        availableHosts={availableHosts}
        manipulateOnus={manipulateOnus}
        manipulateLinks={manipulateLinks}
        manipulateSessions={manipulateSessions}
      />
      <ModalConfig
        hostSelector={
          // <HostsSelector
          //   availableDevices={availableDevices?.cpes}
          //   availableHosts={availableHosts?.cpes}
          //   manipulateCpes={manipulateCpes}
          //   initialData={initialData}
          //   editMode={editMode}
          //   loading={loading}
          //   blockedHostIds={blockedHostIds}
          //   setManipulateCpes={setManipulateCpes}
          //   getInterfaces={getInterfaces}
          //   interfaceList={interfaceList}
          //   type="cpes"
          //   url="/zabbix/interfaceList"
          // />
          /** Configurações de CPE */
          <LinkSelector
            loading={loading}
            editMode={editMode}
            availableHosts={availableHosts?.cpes}
            availableDevices={availableDevices?.cpes}
            manipulateCpes={manipulateCpes}
            setManipulateCpes={setManipulateCpes}
            icon={icons?.links}
            type="cpes"
            url={"/zabbix/interfaceList"}
            title="Hosts:"
            getInterfaces={getInterfaces}
            interfaceList={interfaceList}
            interfaceTags={interfaceTags}
            onCreateTag={createTag}
            onEditTag={editTag}
            onDeleteTag={deleteTag}
          />
        }
        onuSelector={
          /** Configurações de ONU */
          <OnusSelector
            availableDevices={availableDevices?.onus}
            availableHosts={availableHosts?.onus}
            selectedService={selectedService}
            onusFromErp={onusFromErp}
            loading={loading}
            editMode={editMode}
            initialData={initialData}
            manipulateOnus={manipulateOnus}
            setManipulateOnus={setManipulateOnus}
            manipulateOnuItems={manipulateOnuItems}
            setManipulateOnuItems={setManipulateOnuItems}
            onValidityChange={setOnuValid}
          />
        }
        linkSelector={
          /** Configurações de LINK */
          <LinkSelector
            loading={loading}
            editMode={editMode}
            availableHosts={availableHosts?.cpes}
            availableDevices={availableDevices?.cpes}
            manipulateCpes={manipulateLinks}
            setManipulateCpes={setManipulateLinks}
            icon={icons?.links}
            type="links"
            url={"/zabbix/interfaceList"}
            title="Links:"
            getInterfaces={getInterfaces}
            interfaceList={interfaceList}
            interfaceTags={interfaceTags}
            onCreateTag={createTag}
            onEditTag={editTag}
            onDeleteTag={deleteTag}
          />
        }
        sessaoSelector={
          /** Configurações de SESSÃO */
          <LinkSelector
            loading={loading}
            editMode={editMode}
            availableHosts={availableHosts?.cpes}
            availableDevices={availableDevices?.cpes}
            manipulateCpes={manipulateSessions}
            setManipulateCpes={setManipulateSessions}
            icon={icons?.sessions}
            type="sessions"
            url={"/zabbix/sessionsList"}
            title="Sessões:"
            getInterfaces={getInterfaces}
            interfaceList={interfaceList}
            interfaceTags={interfaceTags}
            onCreateTag={createTag}
            onEditTag={editTag}
            onDeleteTag={deleteTag}
          />
        }
        editMode={editMode}
        setEditMode={setEditMode}
        handleSave={saveConfig}
        handleCancel={handleCancel}
        disableSave={!onuValid}
      />
    </Paper>
  );
}

/** OBTER ONUS DO SERVIÇO */
async function getOnuData({
  loading = {},
  selectedService = {},
  clienteId, // dono do serviço: o backend consulta o ERP dele
  setSnackbar = () => {},
  setOnusFromErp = () => {},
}) {
  try {
    loading.onuFromErp = true;
    const response = await api.post("/zabbix/onuList", {
      id_cliente_servico: selectedService?.id?.toString(),
      clienteId,
    });
    setOnusFromErp(response?.data || []);
  } catch (error) {
    console.error(error);
    setSnackbar({
      children: "Error: Falha ao obter onus",
      severity: "error",
    });
  } finally {
    loading.onuFromErp = false;
  }
}

/** SALVAR CONFIGURAÇÕES */
async function handleSave({
  setManipulateCpes = () => {},
  setManipulateOnus = () => {},
  setSnackbar = () => {},
  setEditMode = () => {},
  setChanged = () => {},
  selectedService = {},
  manipulateCpes = {},
  manipulateOnus = {},
  manipulateLinks = {},
  manipulateSessions = {},
  initialData = {},
  clienteInfo = {},
  loading = {},
  interfaceTags = [],
}) {
  try {
    loading.save = true;
    // Filtrar dados para salvar
    const filteredCpes = manipulateCpes?.filter(
      (cpe) => !!cpe?.hostid || !!cpe?.hostId,
    );
    const filteredOnus = manipulateOnus?.filter((onu) => !!onu?.onuItem);

    // Converte interfaces para o contrato { interface, tagIds }, descartando
    // ids de tags que não existem mais (excluídas durante a edição).
    const validTagIds = new Set((interfaceTags ?? []).map((t) => t?.id));
    const toWireIface = (list) =>
      (list ?? []).map((i) => {
        const interfaceName =
          typeof i === "string" ? i : (i?.interface ?? i?.name ?? "");
        const tagIds = (typeof i === "string" ? [] : (i?.tags ?? []))
          .map((t) => t?.id)
          .filter((id) => id && validTagIds.has(id));
        return { interface: interfaceName, tagIds };
      });

    // Formatar dados
    const cpeArr = filteredCpes?.map((cpe) => {
      return {
        groupId: cpe?.groupId,
        hostsIds: [cpe?.hostid || cpe?.hostId],
        hostsClienteInterfaces: toWireIface(cpe?.hostsClienteInterfaces),
        type: "cpe",
      };
    });

    const onuArr = filteredOnus?.map((onu) => {
      return {
        groupId: onu?.groupId,
        hostsIds: [onu?.hostId],
        onuItems: [onu?.onuItem],
        ipOlt: onu?.ipOlt,
        type: "onu",
      };
    });

    const linksArr = manipulateLinks?.map((link) => {
      return {
        groupId: link?.groupId,
        hostsIds: [link?.hostid || link?.hostId],
        hostsClienteInterfaces: toWireIface(link?.hostsClienteInterfaces),
        type: "link",
      };
    });

    const sessionsArr = manipulateSessions?.map((session) => {
      return {
        groupId: session?.groupId,
        hostsIds: [session?.hostid || session?.hostId],
        hostsClienteInterfaces: toWireIface(session?.hostsClienteInterfaces),
        type: "session",
      };
    });

    const formatedData = {
      // Em grupos econômicos, a escrita é roteada ao cliente membro dono do serviço
      clienteId: selectedService?.ownerClienteId || clienteInfo?.id,
      servicoId: selectedService?.id,
      modelos: [...cpeArr, ...onuArr, ...linksArr, ...sessionsArr],
    };

    await api.post("/interno/update/hostsCliente", formatedData);

    // Setar o initialData com os dados atualizados
    initialData.cpes[selectedService.id] = filteredCpes;
    initialData.onus[selectedService.id] = filteredOnus;
    initialData.links[selectedService.id] = manipulateLinks;
    initialData.sessions[selectedService.id] = manipulateSessions;
    // Setar estado atual com os dados atualizados
    setManipulateCpes(filteredCpes);
    setManipulateOnus(filteredOnus);

    setChanged(false);
    setEditMode(false);

    if (filteredOnus?.length > 0) {
      setSnackbar({
        children:
          "Configurações salvas! Importante: as coletas de novas ONUs cadastradas só iniciarão após 30 minutos.",
        severity: "success",
      });
    } else {
      setSnackbar({
        children: "Configurações salvas com sucesso!",
        severity: "success",
      });
    }
  } catch (error) {
    console.error(error);
    setSnackbar({
      children: "Error: Falha atualizar configurações",
      severity: "error",
    });
  } finally {
    loading.save = false;
  }
}

/** OBTER INTERFACES PARA HOST */
async function getInterfaceList({
  setInterfaceList = () => {},
  setSnackbar = () => {},
  interfaceList = {},
  loading = {},
  hostId = "",
  type = "",
  api = {},
  url = "",
}) {
  try {
    if (
      !url ||
      !type ||
      !hostId ||
      (!!loading[type] && !loading.linksAndSessions) ||
      !!interfaceList?.[type]?.[hostId]?.length
    ) {
      return;
    }

    loading[type] = true;

    const response = await api.post(url, {
      hostId: hostId,
    });

    setInterfaceList((prev) => ({
      ...prev,
      [type]: {
        ...(prev?.[type] || {}),
        [hostId]: response?.data || [],
      },
    }));

    return response?.data || [];
  } catch (error) {
    console.error(`Erro ao obter interface do host ${hostId}:`, error);
    setSnackbar({
      children: `Error: Falha ao obter interfaces`,
      severity: "error",
    });
  } finally {
    loading[type] = false;
  }
}
/** OBTER INTERFACES DE TODOS OS HOSTS SELECIONADOS*/
async function getManyInterfaces({
  setSnackbar = () => {},
  setInterfaceList = () => {},
  manipulateCpes = [],
  manipulateSessions = [],
  manipulateLinks = [],
  interfaceList = {},
  loading = {},
  api = {},
}) {
  try {
    loading.linksAndSessions = true;

    const requests = [];

    // CPES
    manipulateCpes?.forEach((cpe) => {
      const hostId = cpe?.hostId || cpe?.hostid;

      if (!hostId || interfaceList?.cpes?.[hostId]?.length) {
        return;
      }

      requests.push(
        api.post("/zabbix/interfaceList", { hostId }).then((response) => ({
          type: "cpes",
          hostId,
          data: response?.data || [],
        })),
      );
    });

    // SESSIONS
    manipulateSessions?.forEach((session) => {
      const hostId = session?.hostId || session?.hostid;

      if (!hostId || interfaceList?.sessions?.[hostId]?.length) {
        return;
      }

      requests.push(
        api.post("/zabbix/sessionsList", { hostId }).then((response) => ({
          type: "sessions",
          hostId,
          data: response?.data || [],
        })),
      );
    });

    // LINKS
    manipulateLinks?.forEach((link) => {
      const hostId = link?.hostId || link?.hostid;

      if (!hostId || interfaceList?.links?.[hostId]?.length) {
        return;
      }

      requests.push(
        api.post("/zabbix/interfaceList", { hostId }).then((response) => ({
          type: "links",
          hostId,
          data: response?.data || [],
        })),
      );
    });

    // AGUARDAR AS RESPOSTAS
    const results = await Promise.all(requests);

    //
    if (results?.length) {
      setInterfaceList((prev) => {
        const next = { ...prev };

        results.forEach(({ type, hostId, data }) => {
          next[type] = {
            ...next[type],
            [hostId]: data,
          };
        });

        return next;
      });
    }
  } catch (error) {
    console.error("Erro ao obter interfaces:", error);
    setSnackbar({
      children: "Error: Falha ao obter interfaces",
      severity: "error",
    });
  } finally {
    loading.linksAndSessions = false;
  }
}
